import { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaSyncAlt } from 'react-icons/fa';
import { PageLayout } from '../../components/layout';
import { Button } from '../../components/Button';
import RegisterOwnerModal from '../../components/KioskRegistrations/RegisterOwnerModal';
import RegistrationDetailDrawer from '../../components/KioskRegistrations/RegistrationDetailDrawer';
import RegistrationStatusBadge from '../../components/KioskRegistrations/RegistrationStatusBadge';
import {
  formatDateTime,
  INPUT_CLASS,
  STATUS_LABELS,
  STATUS_ORDER,
  TYPE_LABELS,
} from '../../components/KioskRegistrations/registrationUi';
import { showErrorToast } from '../../lib/ToastUtility';
import { getKioskRegistrations } from '../../services/KioskRegistrationService';
import type {
  KioskRegistration,
  KioskRegistrationListResponse,
  KioskRegistrationStatus,
  KioskRegistrationType,
} from '../../types/kioskRegistration.types';
import { getApiErrorMessage } from '../../utils/apiError';

const PAGE_SIZE = 25;

export default function KioskRegistrationsPage() {
  const [registrations, setRegistrations] = useState<KioskRegistration[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<KioskRegistrationListResponse['statusCounts']>({});
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<KioskRegistrationStatus | ''>('');
  const [type, setType] = useState<KioskRegistrationType | ''>('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const loadRegistrations = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getKioskRegistrations({
        page,
        limit: PAGE_SIZE,
        status: status || undefined,
        registrationType: type || undefined,
        search: appliedSearch || undefined,
      });
      setRegistrations(result.registrations);
      setTotal(result.total);
      setStatusCounts(result.statusCounts ?? {});
    } catch (error) {
      const message = getApiErrorMessage(error, 'Unable to load channel registrations.');
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, status, type]);

  useEffect(() => {
    void loadRegistrations();
  }, [loadRegistrations]);

  const changeStatus = (next: KioskRegistrationStatus | '') => {
    setPage(1);
    setStatus(next);
  };

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageLayout
      title="Channel Registrations"
      subtitle="Tap2Feed channel owner sign-ups: review, onboarding calls, terms, setup payment and activation."
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" startIcon={<FaSyncAlt className={loading ? 'animate-spin' : ''} />} onClick={() => void loadRegistrations()} disabled={loading}>
            Refresh
          </Button>
          <Button size="sm" startIcon={<FaPlus />} onClick={() => setShowRegister(true)}>
            Register owner
          </Button>
        </div>
      }
    >
      <div className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-wrap gap-2" aria-label="Registrations by status">
          <button
            type="button"
            onClick={() => changeStatus('')}
            aria-pressed={status === ''}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${status === '' ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-300'}`}
          >
            All
          </button>
          {STATUS_ORDER.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => changeStatus(status === value ? '' : value)}
              aria-pressed={status === value}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${status === value ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-300'}`}
            >
              {STATUS_LABELS[value]} · {(statusCounts[value] ?? 0).toLocaleString()}
            </button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_200px_220px_auto]">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applySearch();
            }}
            placeholder="Search name, organisation, email or reference"
            maxLength={255}
            aria-label="Search registrations"
            className={INPUT_CLASS}
          />
          <select
            value={status}
            onChange={(event) => changeStatus(event.target.value as KioskRegistrationStatus | '')}
            aria-label="Filter by status"
            className={INPUT_CLASS}
          >
            <option value="">All statuses</option>
            {STATUS_ORDER.map((value) => (
              <option key={value} value={value}>{STATUS_LABELS[value]}</option>
            ))}
          </select>
          <select
            value={type}
            onChange={(event) => {
              setPage(1);
              setType(event.target.value as KioskRegistrationType | '');
            }}
            aria-label="Filter by registration type"
            className={INPUT_CLASS}
          >
            <option value="">All registration types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <Button size="md" onClick={applySearch}>Search</Button>
        </div>

        {loadError && (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            Could not load registrations: {loadError}
          </p>
        )}

        <p className="text-sm text-gray-600 dark:text-gray-300">
          {total.toLocaleString()} registration{total === 1 ? '' : 's'}
        </p>

        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
          <table className="w-full min-w-[920px] border-collapse text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-800 dark:text-gray-300">
              <tr>
                <th scope="col" className="px-4 py-3">Reference</th>
                <th scope="col" className="px-4 py-3">Name / organisation</th>
                <th scope="col" className="px-4 py-3">Email</th>
                <th scope="col" className="px-4 py-3">Type</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3">Source</th>
                <th scope="col" className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white/60 dark:divide-white/10 dark:bg-slate-900/30">
              {registrations.map((registration) => (
                <tr
                  key={registration.id}
                  tabIndex={0}
                  onClick={() => setSelectedId(registration.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') setSelectedId(registration.id);
                  }}
                  className="cursor-pointer text-gray-700 hover:bg-emerald-50/60 focus:bg-emerald-50/60 focus:outline-none dark:text-gray-200 dark:hover:bg-slate-800/60"
                >
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{registration.referenceCode}</td>
                  <td className="px-4 py-3 font-medium">
                    {[registration.name, registration.organisationName].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td className="px-4 py-3">{registration.email}</td>
                  <td className="whitespace-nowrap px-4 py-3">{TYPE_LABELS[registration.registrationType] ?? registration.registrationType}</td>
                  <td className="px-4 py-3"><RegistrationStatusBadge status={registration.status} channelDeleted={registration.channelDeleted} /></td>
                  <td className="px-4 py-3 text-xs font-semibold">{registration.source}</td>
                  <td className="whitespace-nowrap px-4 py-3">{formatDateTime(registration.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && (
            <div className="px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-300">Loading registrations…</div>
          )}
          {!loading && !loadError && registrations.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-gray-600 dark:text-gray-300">
              No registrations match these filters.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-300">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-gray-200 px-3 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages || loading}
              className="rounded-lg border border-gray-200 px-3 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedId !== null && (
        <RegistrationDetailDrawer
          registrationId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => void loadRegistrations()}
        />
      )}
      {showRegister && (
        <RegisterOwnerModal
          onClose={() => setShowRegister(false)}
          onCreated={(id) => {
            setShowRegister(false);
            setSelectedId(id);
            void loadRegistrations();
          }}
        />
      )}
    </PageLayout>
  );
}
